import axios, { CanceledError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { isRetryableLicenseError, withLicenseRetry } from './licenseRetry'

type Context = { token?: string; route: string }
type RetryConfig = InternalAxiosRequestConfig & {
  _licenseRetry?: boolean
  _licenseContext?: Context
}

export function installLicenseRetry(api: AxiosInstance, options: {
  getContext: () => Context
  refreshLoginCaptcha: () => Promise<string>
  onExhausted: () => void
  now?: () => number
  wait?: (ms: number) => Promise<void>
}) {
  api.interceptors.request.use(config => {
    const request = config as RetryConfig
    request._licenseContext ??= options.getContext()
    return config
  })
  api.interceptors.response.use(undefined, async error => {
    const config = error.config as RetryConfig | undefined
    if (!config || config._licenseRetry || error.response?.status !== 503
        || !isRetryableLicenseError(error.response?.data)) throw error
    const context = config._licenseContext!
    let first = true
    let data = config.data
    return withLicenseRetry(async () => {
      if (first) { first = false; throw error }
      return api.request({ ...config, data, _licenseRetry: true } as RetryConfig)
    }, {
      isBusy: value => axios.isAxiosError(value) && value.response?.status === 503
        && isRetryableLicenseError(value.response.data),
      checkCancelled: () => {
        config.cancelToken?.throwIfRequested()
        const current = options.getContext()
        if (config.signal?.aborted || current.token !== context.token || current.route !== context.route) {
          throw new CanceledError()
        }
      },
      beforeRetry: async () => {
        // CAPTCHA tokens are single-use; a rejected login needs a fresh one.
        if (config.url === '/api/user/login') {
          data = { ...(typeof config.data === 'string' ? JSON.parse(config.data) : config.data),
            recaptchaToken: await options.refreshLoginCaptcha() }
        }
      },
      onExhausted: options.onExhausted,
      now: options.now,
      wait: options.wait,
    })
  })
}
